import React, { useEffect, useState } from "react";
import api, { formatErrorDetail } from "@/lib/api";
import { PageHeader, Surface } from "@/components/app/Primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Building2, Plus, MapPin } from "lucide-react";

const REGIONS = ["NCR", "REGION_I", "REGION_II", "REGION_III", "REGION_IV_A", "REGION_IV_B", "REGION_V", "REGION_VI", "REGION_VII", "REGION_VIII", "REGION_IX", "REGION_X", "REGION_XI", "REGION_XII", "REGION_XIII", "CAR", "BARMM"];
const MANAGE = new Set(["super_admin", "hr_admin", "hr_staff"]);

export default function Organization() {
    const { user } = useAuth();
    const canManage = (user?.roles || []).some((r) => MANAGE.has(r.role));
    const [tree, setTree] = useState([]);
    const [brOpen, setBrOpen] = useState(false);
    const [dpOpen, setDpOpen] = useState(false);
    const [br, setBr] = useState({ name: "", code: "", region: "NCR", address: "" });
    const [dp, setDp] = useState({ name: "", code: "", cost_center: "" });
    const [editBranch, setEditBranch] = useState(null); // {id, name, region}

    const load = () => api.get("/hris/organization").then((r) => setTree(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const addBranch = async () => {
        if (!br.name.trim() || !br.code.trim()) return toast.error("Branch name and code are required");
        try { await api.post("/hris/branches", br); toast.success("Branch added"); setBrOpen(false); setBr({ name: "", code: "", region: "NCR", address: "" }); load(); }
        catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };
    const addDept = async () => {
        if (!dp.name.trim() || !dp.code.trim()) return toast.error("Department name and code are required");
        try { await api.post("/hris/departments", dp); toast.success("Department added"); setDpOpen(false); setDp({ name: "", code: "", cost_center: "" }); load(); }
        catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };
    const saveBranchRegion = async () => {
        try { await api.put(`/hris/branches/${editBranch.id}`, { region: editBranch.region }); toast.success("Branch region updated"); setEditBranch(null); load(); }
        catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <div>
            <PageHeader title="Organization" subtitle="Companies, branches, and departments. Branch regions drive each branch's minimum-wage floor in payroll."
                actions={canManage ? (
                    <div className="flex items-center gap-2">
                        <Dialog open={brOpen} onOpenChange={setBrOpen}>
                            <DialogTrigger asChild><button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2" data-testid="add-branch-btn"><Plus className="size-4" /> Branch</button></DialogTrigger>
                            <DialogContent>
                                <DialogHeader><DialogTitle>Add branch</DialogTitle></DialogHeader>
                                <div className="space-y-2">
                                    <div className="grid grid-cols-2 gap-2">
                                        <div><Label>Name</Label><Input value={br.name} onChange={(e) => setBr({ ...br, name: e.target.value })} placeholder="Cebu Branch" data-testid="branch-name" /></div>
                                        <div><Label>Code</Label><Input value={br.code} onChange={(e) => setBr({ ...br, code: e.target.value })} placeholder="CEB" /></div>
                                    </div>
                                    <div><Label>Region (minimum-wage floor)</Label>
                                        <select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={br.region} onChange={(e) => setBr({ ...br, region: e.target.value })}>
                                            {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                                        </select></div>
                                    <div><Label>Address</Label><Input value={br.address} onChange={(e) => setBr({ ...br, address: e.target.value })} /></div>
                                </div>
                                <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={addBranch} data-testid="save-branch">Add branch</button></DialogFooter>
                            </DialogContent>
                        </Dialog>
                        <Dialog open={dpOpen} onOpenChange={setDpOpen}>
                            <DialogTrigger asChild><button className="h-9 px-3 rounded-md border text-sm font-medium flex items-center gap-2" data-testid="add-dept-btn"><Plus className="size-4" /> Department</button></DialogTrigger>
                            <DialogContent>
                                <DialogHeader><DialogTitle>Add department</DialogTitle></DialogHeader>
                                <div className="space-y-2">
                                    <div className="grid grid-cols-2 gap-2">
                                        <div><Label>Name</Label><Input value={dp.name} onChange={(e) => setDp({ ...dp, name: e.target.value })} placeholder="Engineering" data-testid="dept-name" /></div>
                                        <div><Label>Code</Label><Input value={dp.code} onChange={(e) => setDp({ ...dp, code: e.target.value })} placeholder="ENG" /></div>
                                    </div>
                                    <div><Label>Cost center</Label><Input value={dp.cost_center} onChange={(e) => setDp({ ...dp, cost_center: e.target.value })} placeholder="CC-110" /></div>
                                </div>
                                <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={addDept} data-testid="save-dept">Add department</button></DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </div>
                ) : null} />
            <div className="space-y-4">
                {tree.map((node) => (
                    <Surface key={node.company.id} className="p-4">
                        <div className="flex items-start gap-3">
                            <div className="size-10 rounded-md bg-primary/10 text-primary flex items-center justify-center"><Building2 className="size-5" /></div>
                            <div className="flex-1">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="font-display font-semibold">{node.company.name}</h3>
                                        <div className="text-xs text-muted-foreground">Code: {node.company.code} · TIN: {node.company.tin || "—"} · Region: <span className="font-mono">{node.company.region || "NCR"}</span></div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Headcount</div>
                                        <div className="font-display text-xl font-bold tabular-nums">{node.headcount}</div>
                                    </div>
                                </div>
                                {node.branches?.length > 0 && (
                                    <div className="mt-3">
                                        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Branches</div>
                                        <div className="flex flex-wrap gap-2">
                                            {node.branches.map((b) => (
                                                <button key={b.id} className="badge badge-info inline-flex items-center gap-1"
                                                    title={canManage ? "Click to change region" : undefined}
                                                    onClick={() => canManage && setEditBranch({ id: b.id, name: b.name, region: b.region || "NCR" })}>
                                                    <MapPin className="size-3" /> {b.name}{b.region ? ` · ${b.region}` : ""}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <div className="mt-3">
                                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Departments</div>
                                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                                        {node.departments?.map((d) => (
                                            <div key={d.id} className="border border-border rounded-md p-2 flex items-center justify-between">
                                                <div>
                                                    <div className="font-medium text-sm">{d.name}</div>
                                                    <div className="text-[10px] text-muted-foreground font-mono">{d.code} · {d.cost_center || ""}</div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-xs font-mono">{d.headcount}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Surface>
                ))}
            </div>

            {/* Branch region editor */}
            <Dialog open={!!editBranch} onOpenChange={(v) => !v && setEditBranch(null)}>
                <DialogContent className="max-w-sm">
                    <DialogHeader><DialogTitle>Branch region — {editBranch?.name}</DialogTitle></DialogHeader>
                    <div>
                        <Label>Region (drives the minimum-wage floor)</Label>
                        <select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={editBranch?.region || "NCR"} onChange={(e) => setEditBranch({ ...editBranch, region: e.target.value })}>
                            {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                    </div>
                    <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={saveBranchRegion}>Save</button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
